'use client';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { documentSlug } from '@/lib/utils/documentMetadata';
import documentServices from '@/services/document-services';
import { format, isValid, parse } from 'date-fns';
import { History, Loader2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LegislationHistory } from './legislation-history';

interface LegislationVersionDatePickerProps {
  /** The IRI of the current legislation (e.g., "SK/ZZ/2007/621/20100101") */
  iri: string;
  /** The document content ID of the current legislation */
  documentId: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Parse a Slovak date string (DD.MM.YYYY) to Date object
 */
function parseSlovakDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const parsed = parse(dateStr, 'dd.MM.yyyy', new Date());
  return isValid(parsed) ? parsed : null;
}

/**
 * Format date to Slovak format (DD.MM.YYYY)
 */
function formatToSlovakDate(date: Date): string {
  return format(date, 'dd.MM.yyyy');
}

/**
 * Parse URL query date (DD.MM.YYYY) to input date format (YYYY-MM-DD)
 */
function parseQueryDateToInputFormat(queryDate: string): string | null {
  const parsed = parseSlovakDate(queryDate);
  if (!parsed) return null;
  return format(parsed, 'yyyy-MM-dd');
}

/**
 * LegislationVersionDatePicker - Clean inline date picker for viewing legislation versions
 *
 * Features:
 * - Defaults to today's date
 * - Reads from URL query parameter `ucinnost=DD.MM.YYYY` if present
 * - Updates URL and calls API only on blur (not on every keystroke)
 * - Uses native browser date picker for smooth UX
 */
export function LegislationVersionDatePicker({
  iri,
  documentId,
  className,
}: LegislationVersionDatePickerProps) {
  const locale = useLocale();
  const t = useTranslations('LegislationDetails');
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [initialDate, setInitialDate] = useState<string>('');
  const hasInitialized = useRef(false);

  // Initialize date from URL query or today's date
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const ucinnostParam = searchParams.get('ucinnost');

    if (ucinnostParam) {
      const inputDate = parseQueryDateToInputFormat(ucinnostParam);
      if (inputDate) {
        setSelectedDate(inputDate);
        setInitialDate(inputDate);
        return;
      }
    }

    // Default to today's date
    const today = format(new Date(), 'yyyy-MM-dd');
    setSelectedDate(today);
    setInitialDate(today);
  }, [searchParams]);

  // Handle local date change (just update state, no API call)
  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedDate(e.target.value);
    },
    []
  );

  // Handle blur - call API and redirect if needed
  const handleBlur = useCallback(async () => {
    if (!selectedDate || selectedDate === initialDate) return;

    const targetDate = new Date(selectedDate);
    if (!isValid(targetDate)) return;

    const slovakDate = formatToSlovakDate(targetDate);

    // Update URL
    const params = new URLSearchParams(searchParams.toString());
    params.set('ucinnost', slovakDate);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });

    // Call API to resolve the correct legislation version
    setIsLoading(true);
    try {
      const response = await documentServices.resolveLegislationVersion(
        documentId,
        slovakDate
      );

      if (response?.data?.data) {
        const { document_content_id, is_same } = response.data.data;

        // If the resolved version is different, redirect to the correct document
        if (!is_same) {
          window.location.href = `/${locale}/${documentSlug(locale)}/${document_content_id}?ucinnost=${encodeURIComponent(slovakDate)}`;
        } else {
          // Update initial date since we've confirmed this is the right version
          setInitialDate(selectedDate);
        }
      }
    } catch (error) {
      console.error('Error resolving legislation version:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedDate,
    initialDate,
    documentId,
    locale,
    searchParams,
    pathname,
    router,
  ]);

  const tCommon = useTranslations('Common');

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      <span className='text-sm text-content-secondary'>
        {t('versionDatePicker.showVersionAsOf')}
      </span>
      <div className='relative inline-flex items-center'>
        <input
          type='date'
          data-testid='version-selector'
          value={selectedDate}
          onChange={handleDateChange}
          onBlur={handleBlur}
          className={cn(
            'px-2 py-1.5 border border-line-default rounded text-sm',
            'focus:outline-none focus:ring-1 focus:ring-brand-primary-color focus:border-brand-primary-color',
            'hover:border-line-strong transition-colors',
            'bg-surface-base text-content-primary min-w-[120px]',
            'date-picker-blue-icon'
          )}
          disabled={isLoading}
        />
        {isLoading && (
          <Loader2 className='absolute right-2 h-4 w-4 animate-spin text-content-muted' />
        )}
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='relative flex-shrink-0'>
              <LegislationHistory
                iri={iri}
                className='hidden'
                containerClassName='absolute inset-0 opacity-0 pointer-events-none'
              />
              <button
                type='button'
                className='inline-flex items-center justify-center h-[34px] w-[34px] bg-brand-primary-color/10 hover:bg-brand-primary-color/20 rounded transition-all duration-200 text-brand-primary-color'
                onClick={() => {
                  const historyButton = document.getElementById(
                    'legislation-history-trigger'
                  ) as HTMLButtonElement;
                  if (historyButton) {
                    historyButton.click();
                  }
                }}
                aria-label={tCommon('viewLegislationHistory')}
              >
                <History className='w-[18px] h-[18px]' strokeWidth={2} />
              </button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tCommon('viewLegislationHistory')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
